/**
 * Webhook Service — Admin API wrapper
 * Phase 4.7.5 (WQ-170)
 */

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

export interface WebhookEndpoint {
  id: string;
  tenant_id: string | null;
  name: string;
  url: string;
  secret: string;
  headers: Record<string, string>;
  event_filter: string[];
  min_severity: string;
  is_active: boolean;
  last_delivery_at: string | null;
  last_delivery_status: string | null;
  consecutive_failures: number;
  auto_disable_after: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  secret_plaintext?: string;
}

export interface WebhookDelivery {
  id: string;
  webhook_endpoint_id: string;
  notification_id: string;
  attempt: number;
  status: string;
  http_status: number | null;
  response_body: string | null;
  error_message: string | null;
  latency_ms: number | null;
  next_retry_at: string | null;
  created_at: string;
}

const BASE = '/api/admin/webhooks';

export async function listWebhooks(authFetch: FetchFn): Promise<WebhookEndpoint[]> {
  const res = await authFetch(`${BASE}/`);
  if (!res.ok) throw new Error(`listWebhooks: ${res.status}`);
  return res.json();
}

export async function createWebhook(
  authFetch: FetchFn,
  data: { name: string; url: string; event_filter?: string[]; min_severity?: string; auto_disable_after?: number },
): Promise<WebhookEndpoint> {
  const res = await authFetch(`${BASE}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`createWebhook: ${res.status}`);
  return res.json();
}

export async function getWebhook(authFetch: FetchFn, id: string): Promise<WebhookEndpoint> {
  const res = await authFetch(`${BASE}/${id}`);
  if (!res.ok) throw new Error(`getWebhook: ${res.status}`);
  return res.json();
}

export async function updateWebhook(
  authFetch: FetchFn, id: string, updates: Partial<WebhookEndpoint>,
): Promise<WebhookEndpoint> {
  const res = await authFetch(`${BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`updateWebhook: ${res.status}`);
  return res.json();
}

export async function deleteWebhook(authFetch: FetchFn, id: string): Promise<void> {
  const res = await authFetch(`${BASE}/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteWebhook: ${res.status}`);
}

export async function testWebhook(authFetch: FetchFn, id: string): Promise<Record<string, unknown>> {
  const res = await authFetch(`${BASE}/${id}/test`, { method: 'POST' });
  if (!res.ok) throw new Error(`testWebhook: ${res.status}`);
  return res.json();
}

export async function getDeliveries(authFetch: FetchFn, id: string, limit = 50): Promise<WebhookDelivery[]> {
  const res = await authFetch(`${BASE}/${id}/deliveries?limit=${limit}`);
  if (!res.ok) throw new Error(`getDeliveries: ${res.status}`);
  return res.json();
}
